import { definePreset } from '@primeuix/themes';
import Material from '@primeuix/themes/material';

const EmpluPrimeTheme = definePreset(Material, {
    primitive: {
        borderRadius: {
            none: '0',
            xs: '2px',
            sm: '4px',
            md: '6px',
            lg: '8px',
            xl: '12px'
        },
        green: {
            50: '#f0fdf4',
            100: '#dcfce7',
            200: '#bbf7d0',
            300: 'rgba(221, 246, 224, 1)',
            400: '#4ade80',
            500: 'rgba(0, 200, 0, 1)',
            600: 'rgb(1, 136, 1)',
            700: '#15803d',
            800: '#166534',
            900: '#14532d',
            950: '#052e16'
        }
    },
    semantic: {
        formField: {
            paddingX: '0.75rem',
            paddingY: '0.75rem',
            hoverBorderColor: 'rgba(226, 232, 240, 1)',
            sm: {
                fontSize: '0.875rem',
                paddingX: '0.625rem',
                paddingY: '0.375rem'
            },
            lg: {
                fontSize: '1.125rem',
                paddingX: '0.875rem',
                paddingY: '0.625rem'
            },
            borderRadius: '{border.radius.md}',
            focusRing: {
                width: '0',
                style: 'none',
                color: 'transparent',
                offset: '0',
                shadow: 'none'
            },
            transitionDuration: '{transition.duration}'
        }
    },
    components: {
        accordion: {
            root: {
                transitionDuration: '{transition.duration}'
            },
            panel: {
                borderWidth: '0 0 1px 0',
                borderColor: '{content.border.color}'
            },
            header: {
                color: 'rgba(50, 50, 50, 1)',
                hoverColor: 'rgba(34, 34, 34, 1)',
                activeColor: 'rgba(34, 34, 34, 1)',
                activeHoverColor: 'rgba(50, 50, 50, 1)',
                padding: '1.125rem',
                fontWeight: '600',
                borderRadius: '0',
                borderWidth: '0',
                borderColor: '{content.border.color}',
                background: '{content.background}',
                hoverBackground: 'rgb(246, 246, 246, 1)',
                activeBackground: 'white',
                activeHoverBackground: 'white',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '-1px',
                    shadow: '{focus.ring.shadow}'
                },
                toggleIcon: {
                    color: '{text.muted.color}',
                    hoverColor: '{text.color}',
                    activeColor: '{text.color}',
                    activeHoverColor: '{text.color}'
                },
                first: {
                    topBorderRadius: '{content.border.radius}',
                    borderWidth: '0'
                },
                last: {
                    bottomBorderRadius: '{content.border.radius}',
                    activeBottomBorderRadius: '0'
                }
            },
            content: {
                borderWidth: '0',
                borderColor: '{content.border.color}',
                background: 'white',
                color: '{text.color}',
                padding: '0 1.125rem 1.125rem 1.125rem'
            }
        },
        autocomplete: {
            root: {
                background: '{form.field.background}',
                disabledBackground: '{form.field.disabled.background}',
                filledBackground: '{form.field.filled.background}',
                filledHoverBackground: '{form.field.filled.hover.background}',
                filledFocusBackground: '{form.field.filled.focus.background}',
                borderColor: 'rgba(226, 232, 240, 1)',
                hoverBorderColor: 'rgba(50, 50, 50, 1)',
                focusBorderColor: 'rgba(50, 50, 50, 1)',
                invalidBorderColor: 'rgba(255, 51, 84, 1)',
                color: 'rgba(50, 50, 50, 1)',
                disabledColor: '{form.field.disabled.color}',
                placeholderColor: '{form.field.placeholder.color}',
                invalidPlaceholderColor: 'rgba(255, 51, 84, 1)',
                shadow: '{form.field.shadow}',
                paddingX: '{form.field.padding.x}',
                paddingY: '{form.field.padding.y}',
                borderRadius: '{form.field.border.radius}',
                focusRing: {
                    width: '{form.field.focus.ring.width}',
                    style: '{form.field.focus.ring.style}',
                    color: '{form.field.focus.ring.color}',
                    offset: '{form.field.focus.ring.offset}',
                    shadow: '{form.field.focus.ring.shadow}'
                },
                transitionDuration: '{form.field.transition.duration}'
            },
            overlay: {
                background: '{overlay.select.background}',
                borderColor: '{overlay.select.border.color}',
                borderRadius: '{overlay.select.border.radius}',
                color: '{overlay.select.color}',
                shadow: '{overlay.select.shadow}'
            },
            list: {
                padding: '{list.padding}',
                gap: '{list.gap}'
            },
            option: {
                focusBackground: '{list.option.focus.background}',
                selectedBackground: '{list.option.selected.background}',
                selectedFocusBackground: '{list.option.selected.focus.background}',
                color: '{list.option.color}',
                focusColor: '{list.option.focus.color}',
                selectedColor: '{list.option.selected.color}',
                selectedFocusColor: '{list.option.selected.focus.color}',
                padding: '{list.option.padding}',
                borderRadius: '{list.option.border.radius}'
            },
            optionGroup: {
                background: '{list.option.group.background}',
                color: '{list.option.group.color}',
                fontWeight: '{list.option.group.font.weight}',
                padding: '{list.option.group.padding}'
            },
            dropdown: {
                width: '2.5rem',
                sm: {
                    width: '2rem'
                },
                lg: {
                    width: '3rem'
                },
                borderColor: 'rgba(226, 232, 240, 1)',
                hoverBorderColor: 'rgba(50, 50, 50, 1)',
                activeBorderColor: 'rgba(50, 50, 50, 1)',
                borderRadius: '{form.field.border.radius}',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '{focus.ring.offset}',
                    shadow: '{focus.ring.shadow}'
                }
            },
            chip: {
                borderRadius: '{border.radius.sm}'
            },
            emptyMessage: {
                padding: '{list.option.padding}'
            },
            colorScheme: {
                light: {
                    chip: {
                        focusBackground: '{surface.200}',
                        focusColor: '{surface.800}'
                    },
                    dropdown: {
                        background: '{surface.100}',
                        hoverBackground: '{surface.200}',
                        activeBackground: '{surface.300}',
                        color: '{surface.600}',
                        hoverColor: '{surface.700}',
                        activeColor: '{surface.800}'
                    }
                },
                dark: {
                    chip: {
                        focusBackground: '{surface.700}',
                        focusColor: '{surface.0}'
                    },
                    dropdown: {
                        background: '{surface.800}',
                        hoverBackground: '{surface.700}',
                        activeBackground: '{surface.600}',
                        color: '{surface.300}',
                        hoverColor: '{surface.200}',
                        activeColor: '{surface.100}'
                    }
                }
            }
        },
        button: {
            root: {
                borderRadius: '{border.radius.md}',
                roundedBorderRadius: '2rem',
                gap: '0.5rem',
                iconOnlyWidth: '2rem',
                sm: {
                    fontSize: '{form.field.sm.font.size}',
                    paddingX: '{form.field.sm.padding.x}',
                    paddingY: '{form.field.sm.padding.y}',
                    iconOnlyWidth: '2rem'
                },
                lg: {
                    fontSize: '14px',
                    paddingX: '10.505px',
                    paddingY: '10.505px'
                },
                label: {
                    fontWeight: '500'
                },
                raisedShadow: '0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12)',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    offset: '{focus.ring.offset}'
                },
                badgeSize: '1rem',
                transitionDuration: '{form.field.transition.duration}'
            },
            colorScheme: {
                light: {
                    root: {
                        primary: {
                            background: 'rgba(254, 112, 98, 1)',
                            hoverBackground: 'rgba(224, 74, 72, 1)',
                            activeBackground: 'rgba(224, 74, 72, 1)',
                            borderColor: 'transparent',
                            hoverBorderColor: 'transparent',
                            activeBorderColor: 'transparent',
                            color: '{primary.contrast.color}',
                            hoverColor: '{primary.contrast.color}',
                            activeColor: '{primary.contrast.color}',
                            focusRing: {
                                color: '{primary.color}',
                                shadow: 'none'
                            }
                        },
                        secondary: {
                            background: '{surface.100}',
                            hoverBackground: '{surface.200}',
                            activeBackground: '{surface.300}',
                            borderColor: '{surface.100}',
                            hoverBorderColor: '{surface.200}',
                            activeBorderColor: '{surface.300}',
                            color: 'rgba(50, 50, 50, 1)',
                            hoverColor: 'rgba(34, 34, 34, 1)',
                            activeColor: 'rgba(34, 34, 34, 1)',
                            focusRing: {
                                color: '{surface.600}',
                                shadow: 'none'
                            }
                        },
                        success: {
                            background: '{green.500}',
                            hoverBackground: '{green.600}',
                            activeBackground: '{green.700}',
                            borderColor: '{green.500}',
                            hoverBorderColor: '{green.600}',
                            activeBorderColor: '{green.700}',
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff',
                            focusRing: {
                                color: '{green.500}',
                                shadow: 'none'
                            }
                        },
                        info: {
                            background: '{sky.500}',
                            hoverBackground: '{sky.600}',
                            activeBackground: '{sky.700}',
                            borderColor: '{sky.500}',
                            hoverBorderColor: '{sky.600}',
                            activeBorderColor: '{sky.700}',
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff',
                            focusRing: {
                                color: '{sky.500}',
                                shadow: 'none'
                            }
                        },
                        warn: {
                            background: 'rgba(255, 187, 0, 1)',
                            hoverBackground: 'rgb(203, 149, 5)',
                            activeBackground: '{orange.700}',
                            borderColor: 'rgba(255, 187, 0, 1)',
                            hoverBorderColor: 'rgb(203, 149, 5)',
                            activeBorderColor: '{orange.700}',
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff',
                            focusRing: {
                                color: '{orange.500}',
                                shadow: 'none'
                            }
                        },
                        help: {
                            background: 'rgba(166, 99, 202, 1)',
                            hoverBackground: '{purple.600}',
                            activeBackground: '{purple.700}',
                            borderColor: 'rgba(166, 99, 202, 1)',
                            hoverBorderColor: '{purple.600}',
                            activeBorderColor: '{purple.700}',
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff',
                            focusRing: {
                                color: '{purple.500}',
                                shadow: 'none'
                            }
                        },
                        danger: {
                            background: '{red.500}',
                            hoverBackground: '{red.600}',
                            activeBackground: '{red.700}',
                            borderColor: '{red.500}',
                            hoverBorderColor: '{red.600}',
                            activeBorderColor: '{red.700}',
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff',
                            focusRing: {
                                color: '{red.500}',
                                shadow: 'none'
                            }
                        },
                        contrast: {
                            background: 'rgba(50, 50, 50, 1)',
                            hoverBackground: 'rgba(34, 34, 34, 1)',
                            activeBackground: 'rgba(34, 34, 34, 1)',
                            borderColor: 'rgba(50, 50, 50, 1)',
                            hoverBorderColor: 'rgba(34, 34, 34, 1)',
                            activeBorderColor: 'rgba(34, 34, 34, 1)',
                            color: '{surface.0}',
                            hoverColor: '{surface.0}',
                            activeColor: '{surface.0}',
                            focusRing: {
                                color: '{surface.950}',
                                shadow: 'none'
                            }
                        }
                    },
                    text: {
                        primary: {
                            hoverBackground: 'rgba(254, 112, 98, 0.1)',
                            activeBackground: 'rgba(254, 112, 98, 0.2)',
                            color: 'rgba(254, 112, 98, 1)'
                        },
                        secondary: {
                            hoverBackground: '{surface.50}',
                            activeBackground: '{surface.100}',
                            color: '{surface.500}'
                        },
                        success: {
                            hoverBackground: 'rgba(0, 200, 0, 0.1)',
                            activeBackground: 'rgba(0, 200, 0, 0.2)',
                            color: 'rgba(0, 200, 0, 1)'
                        },
                        info: {
                            hoverBackground: '{sky.50}',
                            activeBackground: '{sky.100}',
                            color: '{sky.500}'
                        },
                        warn: {
                            hoverBackground: '{orange.50}',
                            activeBackground: '{orange.100}',
                            color: '{orange.500}'
                        },
                        help: {
                            hoverBackground: 'rgba(166, 99, 202, 0.1)',
                            activeBackground: 'rgba(166, 99, 202, 0.2)',
                            color: 'rgba(166, 99, 202, 1)'
                        },
                        danger: {
                            hoverBackground: 'rgba(255, 51, 84, 0.1)',
                            activeBackground: 'rgba(255, 51, 84, 0.2)',
                            color: 'rgba(255, 51, 84, 1)'
                        },
                        contrast: {
                            hoverBackground: '{surface.50}',
                            activeBackground: '{surface.100}',
                            color: '{surface.950}'
                        },
                        plain: {
                            hoverBackground: '{surface.50}',
                            activeBackground: '{surface.100}',
                            color: '{surface.700}'
                        }
                    },
                    outlined: {
                        primary: {
                            hoverBackground: 'rgba(254, 112, 98, 0.1)',
                            activeBackground: 'rgba(254, 112, 98, 0.2)',
                            borderColor: 'rgba(254, 112, 98, 0.5)',
                            color: 'rgba(254, 112, 98, 1)'
                        },
                        secondary: {
                            hoverBackground: '{surface.50}',
                            activeBackground: '{surface.100}',
                            borderColor: '{surface.200}',
                            color: '{surface.500}'
                        },
                        success: {
                            hoverBackground: ' rgba(0, 200, 0, 0.1)',
                            activeBackground: ' rgba(0, 200, 0, 0.2)',
                            borderColor: ' rgba(0, 200, 0, 0.5)',
                            color: ' rgba(0, 200, 0, 1)'
                        },
                        info: {
                            hoverBackground: '{sky.50}',
                            activeBackground: '{sky.100}',
                            borderColor: '{sky.200}',
                            color: '{sky.500}'
                        },
                        warn: {
                            hoverBackground: 'rgba(255, 187, 0, 0.1)',
                            activeBackground: 'rgba(255, 187, 0, 0.2)',
                            borderColor: 'rgba(255, 187, 0, 0.5)',
                            color: 'rgba(255, 187, 0, 1)'
                        },
                        help: {
                            hoverBackground: 'rgba(166, 99, 202, 0.1)',
                            activeBackground: 'rgba(166, 99, 202, 0.2)',
                            borderColor: 'rgba(166, 99, 202, 0.5)',
                            color: 'rgba(166, 99, 202, 1)'
                        },
                        danger: {
                            hoverBackground: 'rgba(255, 51, 84, 0.1)',
                            activeBackground: 'rgba(255, 51, 84, 0.2)',
                            borderColor: 'rgba(255, 51, 84, 0.5)',
                            color: 'rgba(255, 51, 84, 1)'
                        },
                        contrast: {
                            hoverBackground: 'rgba(50, 50, 50, 0.1)',
                            activeBackground: 'rgba(50, 50, 50, 0.2)',
                            borderColor: 'rgba(50, 50, 50, 0.5)',
                            color: 'rgba(50, 50, 50, 1)'
                        },
                        plain: {
                            hoverBackground: '{surface.50}',
                            activeBackground: '{surface.100}',
                            borderColor: '{surface.200}',
                            color: '{surface.700}'
                        }
                    },
                    link: {
                        color: '{primary.color}',
                        hoverColor: '{primary.color}',
                        activeColor: '{primary.color}'
                    }
                },
                dark: {
                    root: {
                        primary: {
                            background: '{primary.color}',
                            hoverBackground: '{primary.hover.color}',
                            activeBackground: '{primary.active.color}',
                            borderColor: '{primary.color}',
                            hoverBorderColor: '{primary.hover.color}',
                            activeBorderColor: '{primary.active.color}',
                            color: '{primary.contrast.color}',
                            hoverColor: '{primary.contrast.color}',
                            activeColor: '{primary.contrast.color}',
                            focusRing: {
                                color: '{primary.color}',
                                shadow: 'none'
                            }
                        },
                        secondary: {
                            background: '{surface.800}',
                            hoverBackground: '{surface.700}',
                            activeBackground: '{surface.600}',
                            borderColor: '{surface.800}',
                            hoverBorderColor: '{surface.700}',
                            activeBorderColor: '{surface.600}',
                            color: '{surface.300}',
                            hoverColor: '{surface.200}',
                            activeColor: '{surface.100}',
                            focusRing: {
                                color: '{surface.300}',
                                shadow: 'none'
                            }
                        },
                        info: {
                            background: '{sky.400}',
                            hoverBackground: '{sky.300}',
                            activeBackground: '{sky.200}',
                            borderColor: '{sky.400}',
                            hoverBorderColor: '{sky.300}',
                            activeBorderColor: '{sky.200}',
                            color: '{sky.950}',
                            hoverColor: '{sky.950}',
                            activeColor: '{sky.950}',
                            focusRing: {
                                color: '{sky.400}',
                                shadow: 'none'
                            }
                        },
                        success: {
                            background: '{green.400}',
                            hoverBackground: '{green.300}',
                            activeBackground: '{green.200}',
                            borderColor: '{green.400}',
                            hoverBorderColor: '{green.300}',
                            activeBorderColor: '{green.200}',
                            color: '{green.950}',
                            hoverColor: '{green.950}',
                            activeColor: '{green.950}',
                            focusRing: {
                                color: '{green.400}',
                                shadow: 'none'
                            }
                        },
                        warn: {
                            background: '{orange.400}',
                            hoverBackground: '{orange.300}',
                            activeBackground: '{orange.200}',
                            borderColor: '{orange.400}',
                            hoverBorderColor: '{orange.300}',
                            activeBorderColor: '{orange.200}',
                            color: '{orange.950}',
                            hoverColor: '{orange.950}',
                            activeColor: '{orange.950}',
                            focusRing: {
                                color: '{orange.400}',
                                shadow: 'none'
                            }
                        },
                        help: {
                            background: '{purple.400}',
                            hoverBackground: '{purple.300}',
                            activeBackground: '{purple.200}',
                            borderColor: '{purple.400}',
                            hoverBorderColor: '{purple.300}',
                            activeBorderColor: '{purple.200}',
                            color: '{purple.950}',
                            hoverColor: '{purple.950}',
                            activeColor: '{purple.950}',
                            focusRing: {
                                color: '{purple.400}',
                                shadow: 'none'
                            }
                        },
                        danger: {
                            background: '{red.400}',
                            hoverBackground: '{red.300}',
                            activeBackground: '{red.200}',
                            borderColor: '{red.400}',
                            hoverBorderColor: '{red.300}',
                            activeBorderColor: '{red.200}',
                            color: '{red.950}',
                            hoverColor: '{red.950}',
                            activeColor: '{red.950}',
                            focusRing: {
                                color: '{red.400}',
                                shadow: 'none'
                            }
                        },
                        contrast: {
                            background: '{surface.0}',
                            hoverBackground: '{surface.100}',
                            activeBackground: '{surface.200}',
                            borderColor: '{surface.0}',
                            hoverBorderColor: '{surface.100}',
                            activeBorderColor: '{surface.200}',
                            color: '{surface.950}',
                            hoverColor: '{surface.950}',
                            activeColor: '{surface.950}',
                            focusRing: {
                                color: '{surface.0}',
                                shadow: 'none'
                            }
                        }
                    },
                    outlined: {
                        primary: {
                            hoverBackground: 'color-mix(in srgb, {primary.color}, transparent 96%)',
                            activeBackground: 'color-mix(in srgb, {primary.color}, transparent 84%)',
                            borderColor: '{primary.700}',
                            color: '{primary.color}'
                        },
                        secondary: {
                            hoverBackground: 'rgba(255,255,255,0.04)',
                            activeBackground: 'rgba(255,255,255,0.16)',
                            borderColor: '{surface.700}',
                            color: '{surface.400}'
                        },
                        success: {
                            hoverBackground: 'color-mix(in srgb, {green.400}, transparent 96%)',
                            activeBackground: 'color-mix(in srgb, {green.400}, transparent 84%)',
                            borderColor: '{green.700}',
                            color: '{green.400}'
                        },
                        info: {
                            hoverBackground: 'color-mix(in srgb, {sky.400}, transparent 96%)',
                            activeBackground: 'color-mix(in srgb, {sky.400}, transparent 84%)',
                            borderColor: '{sky.700}',
                            color: '{sky.400}'
                        },
                        warn: {
                            hoverBackground: 'color-mix(in srgb, {orange.400}, transparent 96%)',
                            activeBackground: 'color-mix(in srgb, {orange.400}, transparent 84%)',
                            borderColor: '{orange.700}',
                            color: '{orange.400}'
                        },
                        help: {
                            hoverBackground: 'color-mix(in srgb, {purple.400}, transparent 96%)',
                            activeBackground: 'color-mix(in srgb, {purple.400}, transparent 84%)',
                            borderColor: '{purple.700}',
                            color: '{purple.400}'
                        },
                        danger: {
                            hoverBackground: 'color-mix(in srgb, {red.400}, transparent 96%)',
                            activeBackground: 'color-mix(in srgb, {red.400}, transparent 84%)',
                            borderColor: '{red.700}',
                            color: '{red.400}'
                        },
                        contrast: {
                            hoverBackground: '{surface.800}',
                            activeBackground: '{surface.700}',
                            borderColor: '{surface.500}',
                            color: '{surface.0}'
                        },
                        plain: {
                            hoverBackground: '{surface.800}',
                            activeBackground: '{surface.700}',
                            borderColor: '{surface.600}',
                            color: '{surface.0}'
                        }
                    },
                    text: {
                        primary: {
                            hoverBackground: 'color-mix(in srgb, {primary.color}, transparent 96%)',
                            activeBackground: 'color-mix(in srgb, {primary.color}, transparent 84%)',
                            color: '{primary.color}'
                        },
                        secondary: {
                            hoverBackground: '{surface.800}',
                            activeBackground: '{surface.700}',
                            color: '{surface.400}'
                        },
                        success: {
                            hoverBackground: 'color-mix(in srgb, {green.400}, transparent 96%)',
                            activeBackground: 'color-mix(in srgb, {green.400}, transparent 84%)',
                            color: '{green.400}'
                        },
                        info: {
                            hoverBackground: 'color-mix(in srgb, {sky.400}, transparent 96%)',
                            activeBackground: 'color-mix(in srgb, {sky.400}, transparent 84%)',
                            color: '{sky.400}'
                        },
                        warn: {
                            hoverBackground: 'color-mix(in srgb, {orange.400}, transparent 96%)',
                            activeBackground: 'color-mix(in srgb, {orange.400}, transparent 84%)',
                            color: '{orange.400}'
                        },
                        help: {
                            hoverBackground: 'color-mix(in srgb, {purple.400}, transparent 96%)',
                            activeBackground: 'color-mix(in srgb, {purple.400}, transparent 84%)',
                            color: '{purple.400}'
                        },
                        danger: {
                            hoverBackground: 'color-mix(in srgb, {red.400}, transparent 96%)',
                            activeBackground: 'color-mix(in srgb, {red.400}, transparent 84%)',
                            color: '{red.400}'
                        },
                        contrast: {
                            hoverBackground: '{surface.800}',
                            activeBackground: '{surface.700}',
                            color: '{surface.0}'
                        },
                        plain: {
                            hoverBackground: '{surface.800}',
                            activeBackground: '{surface.700}',
                            color: '{surface.0}'
                        }
                    },
                    link: {
                        color: '{primary.color}',
                        hoverColor: '{primary.color}',
                        activeColor: '{primary.color}'
                    }
                }
            }
        },
        checkbox: {
            root: {
                borderRadius: '{border.radius.sm}',
                width: '1.25rem',
                height: '1.25rem',
                background: '{form.field.background}',
                checkedBackground: '#fff',
                checkedHoverBackground: '#fff',
                disabledBackground: '{form.field.disabled.background}',
                filledBackground: '{form.field.filled.background}',
                borderColor: 'rgba(50, 50, 50, 0.3)',
                hoverBorderColor: '{form.field.hover.border.color}',
                focusBorderColor: '{form.field.border.color}',
                checkedBorderColor: 'rgba(50, 50, 50, 0.3)',
                checkedHoverBorderColor: 'rgba(50, 50, 50, 0.8)',
                checkedFocusBorderColor: '{primary.color}',
                checkedDisabledBorderColor: '{form.field.border.color}',
                invalidBorderColor: '{form.field.invalid.border.color}',
                shadow: '{form.field.shadow}',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '{focus.ring.offset}',
                    shadow: '{focus.ring.shadow}'
                },
                transitionDuration: '{form.field.transition.duration}',
                sm: {
                    width: '1rem',
                    height: '1rem'
                },
                lg: {
                    width: '1.5rem',
                    height: '1.5rem'
                }
            },
            icon: {
                size: '0.875rem',
                color: '{form.field.color}',
                checkedColor: 'rgba(0, 200, 0, 1)',
                checkedHoverColor: 'rgba(0, 200, 0, 1)',
                disabledColor: '{form.field.disabled.color}',
                sm: {
                    size: '0.75rem'
                },
                lg: {
                    size: '1rem'
                }
            }
        },
        chip: {
            root: {
                borderRadius: '16px',
                paddingX: '0.5rem',
                paddingY: '0.25rem',
                gap: '0.5rem',
                transitionDuration: '{transition.duration}'
            },
            image: {
                width: '2rem',
                height: '2rem'
            },
            icon: {
                size: '1rem'
            },
            removeIcon: {
                size: '1rem',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '{focus.ring.offset}',
                    shadow: '{form.field.focus.ring.shadow}'
                }
            },
            colorScheme: {
                light: {
                    root: {
                        background: 'rgba(221, 221, 221, 1)',
                        color: 'rgba(50, 50, 50, 1)'
                    },
                    icon: {
                        color: '{surface.800}'
                    },
                    removeIcon: {
                        color: '{surface.800}'
                    }
                },
                dark: {
                    root: {
                        background: '{surface.800}',
                        color: '{surface.0}'
                    },
                    icon: {
                        color: '{surface.0}'
                    },
                    removeIcon: {
                        color: '{surface.0}'
                    }
                }
            }
        },
        contextmenu: {
            root: {
                background: '{content.background}',
                borderColor: '{content.border.color}',
                color: '{content.color}',
                borderRadius: '{content.border.radius}',
                shadow: '{overlay.navigation.shadow}',
                transitionDuration: '{transition.duration}'
            },
            list: {
                padding: '{navigation.list.padding}',
                gap: '{navigation.list.gap}'
            },
            item: {
                focusBackground: '{navigation.item.focus.background}',
                activeBackground: '{navigation.item.active.background}',
                color: 'rgba(50, 50, 50, 1)',
                focusColor: '{navigation.item.focus.color}',
                activeColor: '{navigation.item.active.color}',
                padding: '{navigation.item.padding}',
                borderRadius: '{navigation.item.border.radius}',
                gap: '{navigation.item.gap}',
                icon: {
                    color: '{navigation.item.icon.color}',
                    focusColor: '{navigation.item.icon.focus.color}',
                    activeColor: '{navigation.item.icon.active.color}'
                }
            },
            submenu: {
                mobileIndent: '1rem'
            },
            submenuIcon: {
                size: '{navigation.submenu.icon.size}',
                color: '{navigation.submenu.icon.color}',
                focusColor: '{navigation.submenu.icon.focus.color}',
                activeColor: '{navigation.submenu.icon.active.color}'
            },
            separator: {
                borderColor: '{content.border.color}'
            }
        },
        datatable: {
            root: {
                transitionDuration: '{transition.duration}'
            },
            header: {
                background: '{content.background}',
                borderColor: '{datatable.border.color}',
                color: '{content.color}',
                borderWidth: '0 0 1px 0',
                padding: '0.75rem 0rem'
            },
            headerCell: {
                background: 'rgba(242, 241, 241, 1)',
                hoverBackground: 'rgba(242, 241, 241, 1)',
                selectedBackground: 'rgba(242, 241, 241, 1)',
                borderColor: '{datatable.border.color}',
                color: 'rgba(50, 50, 50, 1)',
                hoverColor: 'rgba(50, 50, 50, 1)',
                selectedColor: 'rgba(50, 50, 50, 1)',
                gap: '0.5rem',
                padding: '1.5rem 1rem',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '-1px',
                    shadow: '{focus.ring.shadow}'
                }
            },
            columnTitle: {
                fontWeight: '600'
            },
            row: {
                background: '{content.background}',
                hoverBackground: 'rgba(243, 243, 243, 1)',
                selectedBackground: 'rgba(243, 243, 243, 1)',
                color: '{content.color}',
                hoverColor: '{content.hover.color}',
                selectedColor: 'rgba(50, 50, 50, 1)',
                focusRing: {
                    width: '0',
                    style: '{focus.ring.style}',
                    color: 'transparent',
                    offset: '-1px',
                    shadow: '{focus.ring.shadow}'
                }
            },
            bodyCell: {
                borderColor: '{datatable.border.color}',
                padding: '0.75rem 1rem'
            },
            footerCell: {
                background: '{content.background}',
                borderColor: '{datatable.border.color}',
                color: '{content.color}',
                padding: '0.75rem 1rem'
            },
            columnFooter: {
                fontWeight: '600'
            },
            footer: {
                background: '{content.background}',
                borderColor: '{datatable.border.color}',
                color: '{content.color}',
                borderWidth: '0 0 1px 0',
                padding: '0.75rem 1rem'
            },
            dropPoint: {
                color: '{primary.color}'
            },
            resizeIndicator: {
                width: '1px',
                color: '{primary.color}'
            },
            sortIcon: {
                color: '{text.muted.color}',
                hoverColor: '{text.hover.muted.color}',
                size: '0.875rem'
            },
            loadingIcon: {
                size: '2rem'
            },
            rowToggleButton: {
                hoverBackground: '{content.hover.background}',
                selectedHoverBackground: '{content.background}',
                color: '{text.muted.color}',
                hoverColor: '{text.color}',
                selectedHoverColor: '{primary.color}',
                size: '1.75rem',
                borderRadius: '50%',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '{focus.ring.offset}',
                    shadow: '{focus.ring.shadow}'
                }
            },
            filter: {
                inlineGap: '0.5rem',
                overlaySelect: {
                    background: '{overlay.select.background}',
                    borderColor: '{overlay.select.border.color}',
                    borderRadius: '{overlay.select.border.radius}',
                    color: '{overlay.select.color}',
                    shadow: '{overlay.select.shadow}'
                },
                overlayPopover: {
                    background: '{overlay.popover.background}',
                    borderColor: '{overlay.popover.border.color}',
                    borderRadius: '{overlay.popover.border.radius}',
                    color: '{overlay.popover.color}',
                    shadow: '{overlay.popover.shadow}',
                    padding: '{overlay.popover.padding}',
                    gap: '0.5rem'
                },
                rule: {
                    borderColor: '{content.border.color}'
                },
                constraintList: {
                    padding: '{list.padding}',
                    gap: '{list.gap}'
                },
                constraint: {
                    focusBackground: '{list.option.focus.background}',
                    selectedBackground: '{list.option.selected.background}',
                    selectedFocusBackground: '{list.option.selected.focus.background}',
                    color: '{list.option.color}',
                    focusColor: '{list.option.focus.color}',
                    selectedColor: '{list.option.selected.color}',
                    selectedFocusColor: '{list.option.selected.focus.color}',
                    separator: {
                        borderColor: '{content.border.color}'
                    },
                    padding: '{list.option.padding}',
                    borderRadius: '{list.option.border.radius}'
                }
            },
            paginatorTop: {
                borderColor: '{datatable.border.color}',
                borderWidth: '0 0 1px 0'
            },
            paginatorBottom: {
                borderColor: '{datatable.border.color}',
                borderWidth: '0 0 1px 0'
            },
            colorScheme: {
                light: {
                    root: {
                        borderColor: '{content.border.color}'
                    },
                    row: {
                        stripedBackground: '{surface.50}'
                    },
                    bodyCell: {
                        selectedBorderColor: '{primary.100}'
                    }
                },
                dark: {
                    root: {
                        borderColor: '{surface.800}'
                    },
                    row: {
                        stripedBackground: '{surface.950}'
                    },
                    bodyCell: {
                        selectedBorderColor: '{primary.900}'
                    }
                }
            }
        },
        datepicker: {
            root: {
                transitionDuration: '{transition.duration}'
            },
            panel: {
                background: '{content.background}',
                borderColor: '{content.border.color}',
                color: '{content.color}',
                borderRadius: '{content.border.radius}',
                shadow: '{overlay.popover.shadow}',
                padding: '{overlay.popover.padding}'
            },
            header: {
                background: '{content.background}',
                borderColor: '{content.border.color}',
                color: '{content.color}',
                padding: '0 0 0.5rem 0'
            },
            title: {
                gap: '0.5rem',
                fontWeight: '500'
            },
            dropdown: {
                width: '2.5rem',
                sm: {
                    width: '2rem'
                },
                lg: {
                    width: '3rem'
                },
                borderColor: 'rgba(226, 232, 240, 1)',
                hoverBorderColor: 'rgba(50, 50, 50, 1)',
                activeBorderColor: '{form.field.border.color}',
                borderRadius: '{form.field.border.radius}',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '{focus.ring.offset}',
                    shadow: '{focus.ring.shadow}'
                }
            },
            inputIcon: {
                color: '{form.field.icon.color}'
            },
            selectMonth: {
                hoverBackground: '{content.hover.background}',
                color: '{content.color}',
                hoverColor: '{content.hover.color}',
                padding: '0.25rem 0.5rem',
                borderRadius: '{content.border.radius}'
            },
            selectYear: {
                hoverBackground: '{content.hover.background}',
                color: '{content.color}',
                hoverColor: '{content.hover.color}',
                padding: '0.25rem 0.5rem',
                borderRadius: '{content.border.radius}'
            },
            group: {
                borderColor: '{content.border.color}',
                gap: '{overlay.popover.padding}'
            },
            dayView: {
                margin: '0.5rem 0 0 0'
            },
            weekDay: {
                padding: '0.25rem',
                fontWeight: '500',
                color: '{content.color}'
            },
            date: {
                hoverBackground: '{content.hover.background}',
                selectedBackground: '{primary.color}',
                rangeSelectedBackground: '{highlight.background}',
                color: '{content.color}',
                hoverColor: '{content.hover.color}',
                selectedColor: '{primary.contrast.color}',
                rangeSelectedColor: '{highlight.color}',
                width: '2rem',
                height: '2rem',
                borderRadius: '50%',
                padding: '0.25rem',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '{focus.ring.offset}',
                    shadow: '{focus.ring.shadow}'
                }
            },
            monthView: {
                margin: '0.5rem 0 0 0'
            },
            month: {
                padding: '0.375rem',
                borderRadius: '{content.border.radius}'
            },
            yearView: {
                margin: '0.5rem 0 0 0'
            },
            year: {
                padding: '0.375rem',
                borderRadius: '{content.border.radius}'
            },
            buttonbar: {
                padding: '0.5rem 0 0 0',
                borderColor: '{content.border.color}'
            },
            timePicker: {
                padding: '0.5rem 0 0 0',
                borderColor: '{content.border.color}',
                gap: '0.5rem',
                buttonGap: '0.25rem'
            },
            colorScheme: {
                light: {
                    dropdown: {
                        background: 'white',
                        hoverBackground: '{surface.200}',
                        activeBackground: '{surface.300}',
                        color: '{surface.600}',
                        hoverColor: '{surface.700}',
                        activeColor: '{surface.800}'
                    },
                    today: {
                        background: '{surface.200}',
                        color: '{surface.900}'
                    }
                },
                dark: {
                    dropdown: {
                        background: '{surface.800}',
                        hoverBackground: '{surface.700}',
                        activeBackground: '{surface.600}',
                        color: '{surface.300}',
                        hoverColor: '{surface.200}',
                        activeColor: '{surface.100}'
                    },
                    today: {
                        background: '{surface.700}',
                        color: '{surface.0}'
                    }
                }
            }
        },
        dialog: {
            root: {
                background: '{overlay.modal.background}',
                borderColor: '{overlay.modal.border.color}',
                color: '{overlay.modal.color}',
                borderRadius: '{overlay.modal.border.radius}',
                shadow: '{overlay.modal.shadow}'
            },
            header: {
                padding: '{overlay.modal.padding}',
                gap: '0.5rem'
            },
            title: {
                fontSize: '1.5rem',
                fontWeight: '600'
            },
            content: {
                padding: '{overlay.modal.padding} {overlay.modal.padding} {overlay.modal.padding} {overlay.modal.padding}'
            },
            footer: {
                padding: '0 {overlay.modal.padding} {overlay.modal.padding} {overlay.modal.padding}',
                gap: '0.5rem'
            }
        },
        divider: {
            root: {
                borderColor: 'rgba(242, 241, 241, 1)'
            },
            content: {
                background: '{content.background}',
                color: '{text.color}'
            },
            horizontal: {
                margin: '0.5rem 0',
                padding: '0 1rem',
                content: {
                    padding: '0 0.5rem'
                }
            },
            vertical: {
                margin: '0 1rem',
                padding: '0.5rem 0',
                content: {
                    padding: '0.5rem 0'
                }
            }
        },
        floatlabel: {
            root: {
                color: 'rgba(50, 50, 50, 1)',
                focusColor: 'rgba(50, 50, 50, 1)',
                activeColor: 'rgba(50, 50, 50, 1)',
                invalidColor: 'rgba(255, 51, 84, 1)',
                transitionDuration: '0.2s',
                positionX: '{form.field.padding.x}',
                positionY: '{form.field.padding.y}',
                fontWeight: '500',
                active: {
                    fontSize: '1rem',
                    fontWeight: '500'
                }
            },
            over: {
                active: {
                    top: '-1.25rem'
                }
            },
            in: {
                input: {
                    paddingTop: '1.5rem',
                    paddingBottom: '{form.field.padding.y}'
                },
                active: {
                    top: '{form.field.padding.y}'
                }
            },
            on: {
                borderRadius: '{border.radius.xs}',
                active: {
                    background: 'white',
                    padding: '0 0.125rem'
                }
            }
        },
        inputgroup: {
            addon: {
                background: '{form.field.background}',
                borderColor: 'rgba(226, 232, 240, 1)',
                color: '{form.field.icon.color}',
                borderRadius: '{form.field.border.radius}',
                padding: '0.5rem',
                minWidth: '2.5rem'
            }
        },
        inputtext: {
            root: {
                background: '{form.field.background}',
                filledBackground: '{form.field.filled.background}',
                filledHoverBackground: '{form.field.filled.hover.background}',
                filledFocusBackground: '{form.field.filled.focus.background}',
                disabledBackground: 'rgba(243, 243, 243, 1)',
                color: 'rgba(50, 50, 50, 1)',
                borderColor: 'rgba(226, 232, 240, 1)',
                hoverBorderColor: 'rgba(50, 50, 50, 1)',
                focusBorderColor: 'rgba(50, 50, 50, 1)',
                invalidBorderColor: 'rgba(255, 51, 84, 1)',
                disabledColor: '{form.field.disabled.color}',
                placeholderColor: '{form.field.placeholder.color}',
                invalidPlaceholderColor: 'rgba(255, 51, 84, 1)',
                shadow: '{form.field.shadow}',
                paddingX: '{form.field.padding.x}',
                paddingY: '{form.field.padding.y}',
                borderRadius: '{border.radius.md}'
            }
        },
        inputotp: {
            root: {
                gap: '0.5rem'
            },
            input: {
                width: '2.5rem',
                sm: {
                    width: '2rem'
                },
                lg: {
                    width: '5.575rem'
                }
            }
        },
        list: {
            option: {
                focusBackground: '{surface.800}',
                selectedBackground: 'red}',
                selectedFocusBackground: '{highlight.focus.background}',
                color: '{text.color}',
                focusColor: '{text.hover.color}',
                selectedColor: 'red',
                selectedFocusColor: '{highlight.focus.color}',
                icon: {
                    color: '{surface.500}',
                    focusColor: '{surface.400}'
                }
            },
            optionGroup: {
                background: 'transparent',
                color: '{text.muted.color}'
            }
        },
        menu: {
            root: {
                background: '{content.background}',
                borderColor: '{content.border.color}',
                color: '{content.color}',
                borderRadius: '8px',
                shadow: '{overlay.navigation.shadow}',
                transitionDuration: '{transition.duration}'
            },
            list: {
                padding: '{navigation.list.padding}',
                gap: '{navigation.list.gap}'
            },
            item: {
                focusBackground: '{navigation.item.focus.background}',
                color: '{navigation.item.color}',
                focusColor: '{navigation.item.focus.color}',
                padding: '1.25rem 1rem',
                borderRadius: '{navigation.item.border.radius}',
                gap: '{navigation.item.gap}',
                icon: {
                    color: '{navigation.item.icon.color}',
                    focusColor: '{navigation.item.icon.focus.color}'
                }
            },
            submenuLabel: {
                padding: '{navigation.submenu.label.padding}',
                fontWeight: '{navigation.submenu.label.font.weight}',
                background: '{navigation.submenu.label.background}',
                color: '{navigation.submenu.label.color}'
            },
            separator: {
                borderColor: '{content.border.color}'
            }
        },
        message: {
            root: {
                borderRadius: '6px',
                borderWidth: '0.5px',
                transitionDuration: '{transition.duration}'
            },
            content: {
                padding: '0.75rem 0.75rem',
                gap: '0.5rem',
                sm: {
                    padding: '0.375rem 0.625rem'
                },
                lg: {
                    padding: '0.625rem 0.875rem'
                }
            },
            text: {
                fontSize: '0.75rem',
                fontWeight: '400',
                sm: {
                    fontSize: '0.875rem'
                },
                lg: {
                    fontSize: '1.125rem'
                }
            },
            icon: {
                size: '1.125rem',
                sm: {
                    size: '1rem'
                },
                lg: {
                    size: '1.25rem'
                }
            },
            closeButton: {
                width: '1.75rem',
                height: '1.75rem',
                borderRadius: '50%',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    offset: '{focus.ring.offset}'
                }
            },
            closeIcon: {
                size: '1rem',
                sm: {
                    size: '0.875rem'
                },
                lg: {
                    size: '1.125rem'
                }
            },
            outlined: {
                root: {
                    borderWidth: '0.5px'
                }
            },
            simple: {
                content: {
                    padding: '0'
                }
            },
            colorScheme: {
                light: {
                    info: {
                        background: 'rgba(166, 99, 202, 1)',
                        borderColor: 'transparent',
                        color: 'white',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {blue.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: 'rgba(166, 99, 202, 1)',
                            focusRing: {
                                color: 'rgba(166, 99, 202, 1)',
                                shadow: 'none'
                            }
                        },
                        outlined: {
                            color: 'rgba(166, 99, 202, 1)',
                            borderColor: 'rgba(166, 99, 202, 1)'
                        },
                        simple: {
                            color: 'rgba(166, 99, 202, 1)'
                        }
                    },
                    success: {
                        background: 'color-mix(in srgb, {green.50}, transparent 5%)',
                        borderColor: '{green.200}',
                        color: '{green.600}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {green.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{green.100}',
                            focusRing: {
                                color: '{green.600}',
                                shadow: 'none'
                            }
                        },
                        outlined: {
                            color: 'rgba(0, 200, 0, 1)',
                            borderColor: 'rgba(0, 200, 0, 1)'
                        },
                        simple: {
                            color: 'rgba(0, 200, 0, 1)'
                        }
                    },
                    warn: {
                        background: 'color-mix(in srgb,{yellow.50}, transparent 5%)',
                        borderColor: '{yellow.200}',
                        color: '{yellow.600}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {yellow.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{yellow.100}',
                            focusRing: {
                                color: '{yellow.600}',
                                shadow: 'none'
                            }
                        },
                        outlined: {
                            color: '{yellow.600}',
                            borderColor: '{yellow.600}'
                        },
                        simple: {
                            color: '{yellow.600}'
                        }
                    },
                    error: {
                        background: 'color-mix(in srgb, {red.50}, transparent 5%)',
                        borderColor: '{red.200}',
                        color: 'rgba(224, 74, 72, 1)',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {red.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{red.100}',
                            focusRing: {
                                color: '{red.600}',
                                shadow: 'none'
                            }
                        },
                        outlined: {
                            color: '{red.600}',
                            borderColor: '{red.600}'
                        },
                        simple: {
                            color: '{red.600}'
                        }
                    },
                    secondary: {
                        background: '{surface.100}',
                        borderColor: '{surface.200}',
                        color: '{surface.600}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {surface.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{surface.200}',
                            focusRing: {
                                color: '{surface.600}',
                                shadow: 'none'
                            }
                        },
                        outlined: {
                            color: '{surface.500}',
                            borderColor: '{surface.500}'
                        },
                        simple: {
                            color: '{surface.500}'
                        }
                    },
                    contrast: {
                        background: '{surface.900}',
                        borderColor: '{surface.950}',
                        color: '{surface.50}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {surface.950}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{surface.800}',
                            focusRing: {
                                color: '{surface.50}',
                                shadow: 'none'
                            }
                        },
                        outlined: {
                            color: '{surface.950}',
                            borderColor: '{surface.950}'
                        },
                        simple: {
                            color: '{surface.950}'
                        }
                    }
                },
                dark: {
                    info: {
                        background: 'color-mix(in srgb, {blue.500}, transparent 84%)',
                        borderColor: 'color-mix(in srgb, {blue.700}, transparent 64%)',
                        color: 'rgba(166, 99, 202, 1)',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {blue.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: 'rgba(255, 255, 255, 0.05)',
                            focusRing: {
                                color: '{blue.500}',
                                shadow: 'none'
                            }
                        },
                        outlined: {
                            color: '{blue.500}',
                            borderColor: '{blue.500}'
                        },
                        simple: {
                            color: '{blue.500}'
                        }
                    },
                    success: {
                        background: 'color-mix(in srgb, {green.500}, transparent 84%)',
                        borderColor: 'color-mix(in srgb, {green.700}, transparent 64%)',
                        color: '{green.500}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {green.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: 'rgba(255, 255, 255, 0.05)',
                            focusRing: {
                                color: '{green.500}',
                                shadow: 'none'
                            }
                        },
                        outlined: {
                            color: '{green.500}',
                            borderColor: '{green.500}'
                        },
                        simple: {
                            color: '{green.500}'
                        }
                    },
                    warn: {
                        background: 'color-mix(in srgb, {yellow.500}, transparent 84%)',
                        borderColor: 'color-mix(in srgb, {yellow.700}, transparent 64%)',
                        color: '{yellow.500}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {yellow.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: 'rgba(255, 255, 255, 0.05)',
                            focusRing: {
                                color: '{yellow.500}',
                                shadow: 'none'
                            }
                        },
                        outlined: {
                            color: '{yellow.500}',
                            borderColor: '{yellow.500}'
                        },
                        simple: {
                            color: '{yellow.500}'
                        }
                    },
                    error: {
                        background: 'color-mix(in srgb, {red.500}, transparent 84%)',
                        borderColor: 'color-mix(in srgb, {red.700}, transparent 64%)',
                        color: '{red.500}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {red.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: 'rgba(255, 255, 255, 0.05)',
                            focusRing: {
                                color: '{red.500}',
                                shadow: 'none'
                            }
                        },
                        outlined: {
                            color: '{red.500}',
                            borderColor: '{red.500}'
                        },
                        simple: {
                            color: '{red.500}'
                        }
                    },
                    secondary: {
                        background: '{surface.800}',
                        borderColor: '{surface.700}',
                        color: '{surface.300}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {surface.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{surface.700}',
                            focusRing: {
                                color: '{surface.300}',
                                shadow: 'none'
                            }
                        },
                        outlined: {
                            color: '{surface.400}',
                            borderColor: '{surface.400}'
                        },
                        simple: {
                            color: '{surface.400}'
                        }
                    },
                    contrast: {
                        background: '{surface.0}',
                        borderColor: '{surface.100}',
                        color: '{surface.950}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {surface.950}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{surface.100}',
                            focusRing: {
                                color: '{surface.950}',
                                shadow: 'none'
                            }
                        },
                        outlined: {
                            color: '{surface.0}',
                            borderColor: '{surface.0}'
                        },
                        simple: {
                            color: '{surface.0}'
                        }
                    }
                }
            }
        },
        overlay: {
            select: {
                borderRadius: '{border.radius.md}',
                shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)'
            },
            popover: {
                borderRadius: '{border.radius.md}',
                padding: '0.75rem',
                shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)'
            },
            modal: {
                borderRadius: '{border.radius.xl}',
                padding: '1.25rem',
                shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
            },
            navigation: {
                shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)'
            }
        },
        paginator: {
            root: {
                padding: '0',
                gap: '0.25rem',
                borderRadius: '{content.border.radius}',
                background: '{content.background}',
                color: '{content.color}',
                transitionDuration: '{transition.duration}'
            },
            navButton: {
                background: 'transparent',
                hoverBackground: '{content.hover.background}',
                selectedBackground: 'rgba(50, 50, 50, 1)',
                color: '{text.muted.color}',
                hoverColor: '{text.hover.muted.color}',
                selectedColor: 'white',
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '8px',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '{focus.ring.offset}',
                    shadow: '{focus.ring.shadow}'
                }
            },
            currentPageReport: {
                color: '{text.muted.color}'
            },
            jumpToPageInput: {
                maxWidth: '2.5rem'
            }
        },
        progressspinner: {
            colorScheme: {
                light: {
                    root: {
                        colorOne: 'rgba(254, 112, 98, 1)',
                        colorTwo: 'rgba(254, 112, 98, 1)',
                        colorThree: 'rgba(254, 112, 98, 1)',
                        colorFour: 'rgba(254, 112, 98, 1)'
                    }
                },
                dark: {
                    root: {
                        colorOne: '{red.400}',
                        colorTwo: '{blue.400}',
                        colorThree: '{green.400}',
                        colorFour: '{yellow.400}'
                    }
                }
            }
        },
        select: {
            root: {
                background: '{form.field.background}',
                disabledBackground: '{form.field.disabled.background}',
                filledBackground: '{form.field.filled.background}',
                filledHoverBackground: '{form.field.filled.hover.background}',
                filledFocusBackground: '{form.field.filled.focus.background}',
                borderColor: 'rgba(226, 232, 240, 1)',
                hoverBorderColor: '{form.field.hover.border.color}',
                focusBorderColor: 'rgba(50, 50, 50, 1)',
                invalidBorderColor: '{form.field.invalid.border.color}',
                color: '{form.field.color}',
                disabledColor: '{form.field.disabled.color}',
                placeholderColor: '{form.field.placeholder.color}',
                invalidPlaceholderColor: '{form.field.invalid.placeholder.color}',
                shadow: '{form.field.shadow}',
                paddingX: '{form.field.padding.x}',
                paddingY: '{form.field.padding.y}',
                borderRadius: '{form.field.border.radius}',
                focusRing: {
                    width: '0.5px',
                    style: '{form.field.focus.ring.style}',
                    color: 'rgba(50, 50, 50, 1)',
                    offset: '{form.field.focus.ring.offset}',
                    shadow: '{form.field.focus.ring.shadow}'
                },
                transitionDuration: '{form.field.transition.duration}',
                sm: {
                    fontSize: '{form.field.sm.font.size}',
                    paddingX: '{form.field.sm.padding.x}',
                    paddingY: '{form.field.sm.padding.y}'
                },
                lg: {
                    fontSize: '{form.field.lg.font.size}',
                    paddingX: '{form.field.lg.padding.x}',
                    paddingY: '{form.field.lg.padding.y}'
                }
            },
            dropdown: {
                width: '2.5rem',
                color: '{form.field.icon.color}'
            },
            overlay: {
                background: '{overlay.select.background}',
                borderColor: '{overlay.select.border.color}',
                borderRadius: '{overlay.select.border.radius}',
                color: '{overlay.select.color}',
                shadow: '{overlay.select.shadow}'
            },
            list: {
                padding: '{list.padding}',
                gap: '{list.gap}',
                header: {
                    padding: '{list.header.padding}'
                }
            },
            option: {
                focusBackground: 'rgba(221, 221, 221, 1)',
                selectedBackground: 'rgba(243, 243, 243, 1)',
                selectedFocusBackground: 'rgba(221, 221, 221, 1)',
                color: 'rgba(50, 50, 50, 1)',
                focusColor: 'rgba(50, 50, 50, 1)',
                selectedColor: 'rgba(50, 50, 50, 1)',
                selectedFocusColor: 'rgba(50, 50, 50, 1)',
                padding: '{list.option.padding}',
                borderRadius: '{list.option.border.radius}'
            },
            optionGroup: {
                background: '{list.option.group.background}',
                color: '{list.option.group.color}',
                fontWeight: '{list.option.group.font.weight}',
                padding: '{list.option.group.padding}'
            },
            clearIcon: {
                color: '{form.field.icon.color}'
            },
            checkmark: {
                color: '{list.option.color}',
                gutterStart: '-0.375rem',
                gutterEnd: '0.375rem'
            },
            emptyMessage: {
                padding: '{list.option.padding}'
            }
        },
        skeleton: {
            root: {
                borderRadius: '{content.border.radius}'
            },
            colorScheme: {
                light: {
                    root: {
                        background: 'rgb(246, 246, 246, 1)',
                        animationBackground: 'rgba(250, 250, 250, 1)'
                    }
                },
                dark: {
                    root: {
                        background: 'rgba(255, 255, 255, 0.06)',
                        animationBackground: 'rgba(255, 255, 255, 0.04)'
                    }
                }
            }
        },
        tabs: {
            root: {
                transitionDuration: '{transition.duration}'
            },
            tablist: {
                borderWidth: '0 0 1px 0',
                background: '{content.background}',
                borderColor: '{content.border.color}'
            },
            tab: {
                background: 'transparent',
                hoverBackground: 'transparent',
                activeBackground: 'transparent',
                borderWidth: '0 0 1px 0',
                borderColor: '{content.border.color}',
                hoverBorderColor: '{content.border.color}',
                activeBorderColor: 'rgba(50, 50, 50, 1)',
                color: 'rgba(50, 50, 50, 1)',
                hoverColor: 'rgba(254, 112, 98, 0.7)',
                activeColor: 'rgba(254, 112, 98, 1)',
                padding: '1rem 1.125rem',
                fontWeight: '500',
                margin: '0 0 -1px 0',
                gap: '0.5rem',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '-1px',
                    shadow: '{focus.ring.shadow}'
                }
            },
            tabpanel: {
                background: '{content.background}',
                color: '{content.color}',
                padding: '0.875rem 1.125rem 1.125rem 1.125rem',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '{focus.ring.offset}',
                    shadow: 'inset {focus.ring.shadow}'
                }
            },
            navButton: {
                background: '{content.background}',
                color: '{text.muted.color}',
                hoverColor: '{text.color}',
                width: '2.5rem',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    color: '{focus.ring.color}',
                    offset: '-1px',
                    shadow: '{focus.ring.shadow}'
                }
            },
            activeBar: {
                height: '1.5px',
                bottom: '-1px',
                background: 'rgba(254, 112, 98, 1)'
            },
            colorScheme: {
                light: {
                    navButton: {
                        shadow: '0px 0px 10px 50px rgba(255, 255, 255, 0.6)'
                    }
                },
                dark: {
                    navButton: {
                        shadow: '0px 0px 10px 50px color-mix(in srgb, {content.background}, transparent 50%)'
                    }
                }
            }
        },
        tabview: {
            root: {
                transitionDuration: '{transition.duration}'
            },
            tabList: {
                background: '{content.background}',
                borderColor: '{content.border.color}'
            },
            tab: {
                borderColor: '{content.border.color}',
                activeBorderColor: '{primary.color}',
                color: '{text.muted.color}',
                hoverColor: '{text.color}',
                activeColor: '{primary.color}'
            },
            tabPanel: {
                background: '{content.background}',
                color: '{content.color}'
            },
            navButton: {
                background: '{content.background}',
                color: '{text.muted.color}',
                hoverColor: '{text.color}'
            },
            colorScheme: {
                light: {
                    navButton: {
                        shadow: '0px 0px 10px 50px rgba(255, 255, 255, 0.6)'
                    }
                },
                dark: {
                    navButton: {
                        shadow: '0px 0px 10px 50px color-mix(in srgb, {content.background}, transparent 50%)'
                    }
                }
            }
        },
        tag: {
            root: {
                fontSize: '0.875rem',
                fontWeight: '700',
                padding: '0.25rem 0.5rem',
                gap: '0.25rem',
                borderRadius: '{content.border.radius}',
                roundedBorderRadius: '{border.radius.xl}'
            },
            icon: {
                size: '0.75rem'
            },
            colorScheme: {
                light: {
                    primary: {
                        background: 'rgba(50, 50, 50, 1)',
                        color: 'white'
                    },
                    secondary: {
                        background: 'rgba(254, 112, 98, 1)',
                        color: 'white'
                    },
                    success: {
                        background: 'rgba(221, 246, 224, 1)',
                        color: 'rgba(0, 200, 0, 1)'
                    },
                    info: {
                        background: '{sky.100}',
                        color: 'rgba(166, 99, 202, 1)'
                    },
                    warn: {
                        background: 'rgba(254, 249, 233, 1)',
                        color: 'rgba(255, 187, 0, 1),'
                    },
                    danger: {
                        background: 'rgba(255, 204, 212, 1',
                        color: 'rgba(255, 51, 84, 1)'
                    },
                    contrast: {
                        background: 'rgba(242, 241, 241, 1)',
                        color: 'rgba(50, 50, 50, 1)'
                    }
                },
                dark: {
                    primary: {
                        background: 'color-mix(in srgb, {primary.500}, transparent 84%)',
                        color: '{primary.300}'
                    },
                    secondary: {
                        background: '{surface.800}',
                        color: '{surface.300}'
                    },
                    success: {
                        background: 'color-mix(in srgb, {green.500}, transparent 84%)',
                        color: '{green.300}'
                    },
                    info: {
                        background: 'color-mix(in srgb, {sky.500}, transparent 84%)',
                        color: 'rgba(166, 99, 202, 1)'
                    },
                    warn: {
                        background: 'color-mix(in srgb, {orange.500}, transparent 84%)',
                        color: '{orange.300}'
                    },
                    danger: {
                        background: 'color-mix(in srgb, {red.500}, transparent 84%)',
                        color: '{red.300}'
                    },
                    contrast: {
                        background: '{surface.0}',
                        color: '{surface.950}'
                    }
                }
            }
        },
        textarea: {
            root: {
                background: '{form.field.background}',
                disabledBackground: 'rgba(243, 243, 243, 1)',
                filledBackground: '{form.field.filled.background}',
                filledHoverBackground: '{form.field.filled.hover.background}',
                filledFocusBackground: '{form.field.filled.focus.background}',
                borderColor: 'rgba(226, 232, 240, 1)',
                hoverBorderColor: 'rgba(50, 50, 50, 1)',
                focusBorderColor: 'rgba(50, 50, 50, 1)',
                invalidBorderColor: 'rgba(255, 51, 84, 1)',
                color: 'rgba(50, 50, 50, 1)',
                disabledColor: '{form.field.disabled.color}',
                placeholderColor: '{form.field.placeholder.color}',
                invalidPlaceholderColor: 'rgba(255, 51, 84, 1)',
                shadow: '{form.field.shadow}',
                paddingX: '{form.field.padding.x}',
                paddingY: '{form.field.padding.y}',
                borderRadius: '{form.field.border.radius}',
                focusRing: {
                    width: '{form.field.focus.ring.width}',
                    style: '{form.field.focus.ring.style}',
                    color: '{form.field.focus.ring.color}',
                    offset: '{form.field.focus.ring.offset}',
                    shadow: '{form.field.focus.ring.shadow}'
                },
                transitionDuration: '{form.field.transition.duration}',
                sm: {
                    fontSize: '{form.field.sm.font.size}',
                    paddingX: '{form.field.sm.padding.x}',
                    paddingY: '{form.field.sm.padding.y}'
                },
                lg: {
                    fontSize: '{form.field.lg.font.size}',
                    paddingX: '{form.field.lg.padding.x}',
                    paddingY: '{form.field.lg.padding.y}'
                }
            }
        },
        tieredmenu: {
            root: {
                background: '{content.background}',
                borderColor: '{content.border.color}',
                color: 'rgba(50, 50, 50, 1)',
                borderRadius: '{content.border.radius}',
                shadow: '{overlay.navigation.shadow}',
                transitionDuration: '{transition.duration}'
            },
            list: {
                padding: '{navigation.list.padding}',
                gap: '{navigation.list.gap}'
            },
            item: {
                focusBackground: '{navigation.item.focus.background}',
                activeBackground: '{navigation.item.active.background}',
                color: 'rgba(50, 50, 50, 1)',
                focusColor: '{navigation.item.focus.color}',
                activeColor: '{navigation.item.active.color}',
                padding: '{navigation.item.padding}',
                borderRadius: '{navigation.item.border.radius}',
                gap: '{navigation.item.gap}',
                icon: {
                    color: '{navigation.item.icon.color}',
                    focusColor: '{navigation.item.icon.focus.color}',
                    activeColor: '{navigation.item.icon.active.color}'
                }
            },
            submenu: {
                mobileIndent: '1rem'
            },
            submenuIcon: {
                size: '{navigation.submenu.icon.size}',
                color: '{navigation.submenu.icon.color}',
                focusColor: '{navigation.submenu.icon.focus.color}',
                activeColor: '{navigation.submenu.icon.active.color}'
            },
            separator: {
                borderColor: '{content.border.color}'
            }
        },
        toast: {
            root: {
                width: '25rem',
                borderRadius: '8px',
                borderWidth: '1px',
                transitionDuration: '{transition.duration}'
            },
            icon: {
                size: '1.25rem'
            },
            content: {
                padding: '{overlay.popover.padding}',
                gap: '0.5rem'
            },
            text: {
                gap: '0.5rem'
            },
            summary: {
                fontWeight: '500',
                fontSize: '1rem'
            },
            detail: {
                fontWeight: '500',
                fontSize: '0.875rem'
            },
            closeButton: {
                width: '1.75rem',
                height: '1.75rem',
                borderRadius: '50%',
                focusRing: {
                    width: '{focus.ring.width}',
                    style: '{focus.ring.style}',
                    offset: '{focus.ring.offset}'
                }
            },
            closeIcon: {
                size: '1rem'
            },
            colorScheme: {
                light: {
                    info: {
                        background: 'color-mix(in srgb, {blue.50}, transparent 5%)',
                        borderColor: '{blue.200}',
                        color: 'rgba(166, 99, 202, 1)',
                        detailColor: '{surface.700}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {blue.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{blue.100}',
                            focusRing: {
                                color: 'rgba(166, 99, 202, 1)',
                                shadow: 'none'
                            }
                        }
                    },
                    success: {
                        background: 'rgba(50, 50, 50, 1)',
                        borderColor: 'transparent',
                        color: 'rgba(0, 200, 0, 1)',
                        detailColor: 'rgba(0, 200, 0, 1)',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {green.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{green.100}',
                            focusRing: {
                                color: '{green.600}',
                                shadow: 'none'
                            }
                        }
                    },
                    warn: {
                        background: 'color-mix(in srgb,{yellow.50}, transparent 5%)',
                        borderColor: '{yellow.200}',
                        color: '{yellow.600}',
                        detailColor: '{surface.700}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {yellow.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{yellow.100}',
                            focusRing: {
                                color: '{yellow.600}',
                                shadow: 'none'
                            }
                        }
                    },
                    error: {
                        background: 'rgba(50, 50, 50, 1)',
                        borderColor: 'transparent',
                        color: 'rgba(255, 51, 84, 1)',
                        detailColor: 'rgba(255, 51, 84, 1)',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {red.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: 'rgba(255, 255, 255, 0.05)',
                            focusRing: {
                                color: '{red.500}',
                                shadow: 'none'
                            }
                        }
                    },
                    secondary: {
                        background: '{surface.100}',
                        borderColor: '{surface.200}',
                        color: '{surface.600}',
                        detailColor: '{surface.700}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {surface.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{surface.200}',
                            focusRing: {
                                color: '{surface.600}',
                                shadow: 'none'
                            }
                        }
                    },
                    contrast: {
                        background: '{surface.900}',
                        borderColor: '{surface.950}',
                        color: '{surface.50}',
                        detailColor: '{surface.0}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {surface.950}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{surface.800}',
                            focusRing: {
                                color: '{surface.50}',
                                shadow: 'none'
                            }
                        }
                    }
                },
                dark: {
                    info: {
                        background: 'color-mix(in srgb, {blue.500}, transparent 84%)',
                        borderColor: 'color-mix(in srgb, {blue.700}, transparent 64%)',
                        color: 'rgba(166, 99, 202, 1)',
                        detailColor: '{surface.0}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {blue.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: 'rgba(255, 255, 255, 0.05)',
                            focusRing: {
                                color: '{blue.500}',
                                shadow: 'none'
                            }
                        }
                    },
                    success: {
                        background: 'color-mix(in srgb, {green.500}, transparent 84%)',
                        borderColor: 'color-mix(in srgb, {green.700}, transparent 64%)',
                        color: '{green.500}',
                        detailColor: '{surface.0}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {green.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: 'rgba(255, 255, 255, 0.05)',
                            focusRing: {
                                color: '{green.500}',
                                shadow: 'none'
                            }
                        }
                    },
                    warn: {
                        background: 'color-mix(in srgb, {yellow.500}, transparent 84%)',
                        borderColor: 'color-mix(in srgb, {yellow.700}, transparent 64%)',
                        color: '{yellow.500}',
                        detailColor: '{surface.0}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {yellow.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: 'rgba(255, 255, 255, 0.05)',
                            focusRing: {
                                color: '{yellow.500}',
                                shadow: 'none'
                            }
                        }
                    },
                    error: {
                        background: 'color-mix(in srgb, {red.500}, transparent 84%)',
                        borderColor: 'color-mix(in srgb, {red.700}, transparent 64%)',
                        color: '{red.500}',
                        detailColor: '{surface.0}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {red.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: 'rgba(255, 255, 255, 0.05)',
                            focusRing: {
                                color: '{red.500}',
                                shadow: 'none'
                            }
                        }
                    },
                    secondary: {
                        background: '{surface.800}',
                        borderColor: '{surface.700}',
                        color: '{surface.300}',
                        detailColor: '{surface.0}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {surface.500}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{surface.700}',
                            focusRing: {
                                color: '{surface.300}',
                                shadow: 'none'
                            }
                        }
                    },
                    contrast: {
                        background: '{surface.0}',
                        borderColor: '{surface.100}',
                        color: '{surface.950}',
                        detailColor: '{surface.950}',
                        shadow: '0px 4px 8px 0px color-mix(in srgb, {surface.950}, transparent 96%)',
                        closeButton: {
                            hoverBackground: '{surface.100}',
                            focusRing: {
                                color: '{surface.950}',
                                shadow: 'none'
                            }
                        }
                    }
                }
            }
        }
    }
});

export default EmpluPrimeTheme;
